import { useState, type FormEvent } from 'react';
import { MapPin, Pencil, Plus, Star, Trash2 } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Skeleton,
  Table,
  Td,
  Th,
} from '@/components/ui';
import {
  useDeleteWarehouse,
  useSaveWarehouse,
  useSetDefaultWarehouse,
  useWarehouses,
  type Warehouse,
} from '@/lib/inventory-api';
import { errorMessage, fieldErrors, formatNumber } from '@/lib/utils';

interface FormState {
  name: string;
  code: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  managerName: string;
  managerPhone: string;
  isActive: boolean;
}

const EMPTY: FormState = {
  name: '',
  code: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  pincode: '',
  managerName: '',
  managerPhone: '',
  isActive: true,
};

/**
 * Warehouses (spec §25).
 *
 * Exactly one is the default. Promoting another demotes the incumbent in a
 * single request, so there is never a moment with none or two.
 */
export function WarehousesPage() {
  const warehouses = useWarehouses();
  const remove = useDeleteWarehouse();
  const setDefault = useSetDefaultWarehouse();
  const [editing, setEditing] = useState<Warehouse | null | undefined>(undefined);

  const failure = remove.error ?? setDefault.error;

  return (
    <>
      <PageHeader
        title="Warehouses"
        description="Stock locations. A warehouse holding stock must be emptied before it can be deleted."
        action={
          <Button size="sm" onClick={() => setEditing(null)}>
            <Plus className="size-4" aria-hidden="true" />
            Add Warehouse
          </Button>
        }
      />

      {failure && (
        <div className="mb-4">
          <ErrorAlert>{errorMessage(failure)}</ErrorAlert>
        </div>
      )}

      <Card>
        {warehouses.isPending && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </div>
        )}

        {warehouses.isError && (
          <div className="p-4">
            <ErrorAlert title="Could not load warehouses">
              {errorMessage(warehouses.error)}
            </ErrorAlert>
          </div>
        )}

        {warehouses.data?.length === 0 && (
          <div className="p-4">
            <EmptyState
              title="No warehouses yet"
              description="The first warehouse you create becomes the default automatically."
            />
          </div>
        )}

        {warehouses.data && warehouses.data.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Warehouse</Th>
                <Th>Location</Th>
                <Th>Manager</Th>
                <Th className="text-right">Products</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {warehouses.data.map((warehouse) => (
                <tr key={warehouse.id} className="hover:bg-canvas">
                  <Td>
                    <p className="font-medium text-ink">{warehouse.name}</p>
                    <p className="font-mono text-xs text-ink-subtle">{warehouse.code}</p>
                  </Td>

                  <Td className="text-ink-muted">
                    <span className="flex items-start gap-1.5">
                      <MapPin
                        className="mt-0.5 size-3.5 shrink-0 text-ink-subtle"
                        aria-hidden="true"
                      />
                      <span>
                        {warehouse.city}, {warehouse.state}
                        <span className="block text-xs text-ink-subtle">{warehouse.pincode}</span>
                      </span>
                    </span>
                  </Td>

                  <Td className="text-ink-muted">
                    {warehouse.managerName ?? '—'}
                    {warehouse.managerPhone && (
                      <span className="block text-xs text-ink-subtle">
                        {warehouse.managerPhone}
                      </span>
                    )}
                  </Td>

                  <Td className="text-right text-ink-muted">
                    {formatNumber(warehouse.stockedProductCount)}
                  </Td>

                  <Td>
                    <div className="flex flex-wrap gap-1">
                      <Badge tone={warehouse.isActive ? 'success' : 'neutral'}>
                        {warehouse.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {warehouse.isDefault && <Badge tone="brand">Default</Badge>}
                    </div>
                  </Td>

                  <Td>
                    <div className="flex justify-end gap-1">
                      {!warehouse.isDefault && warehouse.isActive && (
                        <button
                          type="button"
                          onClick={() => setDefault.mutate(warehouse.id)}
                          aria-label={`Make ${warehouse.name} the default`}
                          title="Make default"
                          className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-brand-600"
                        >
                          <Star className="size-4" aria-hidden="true" />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setEditing(warehouse)}
                        aria-label={`Edit ${warehouse.name}`}
                        className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-brand-600"
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </button>

                      {!warehouse.isDefault && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Delete "${warehouse.name}"?`)) {
                              remove.mutate(warehouse.id);
                            }
                          }}
                          aria-label={`Delete ${warehouse.name}`}
                          className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-danger-50 hover:text-danger-600"
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <WarehouseDrawer
        open={editing !== undefined}
        warehouse={editing ?? null}
        onClose={() => setEditing(undefined)}
      />
    </>
  );
}

/** Mounted only while open, so its state resets by unmounting. */
function WarehouseDrawer({
  open,
  warehouse,
  onClose,
}: {
  open: boolean;
  warehouse: Warehouse | null;
  onClose: () => void;
}) {
  if (!open) {
    return null;
  }

  return <WarehouseForm key={warehouse?.id ?? 'new'} warehouse={warehouse} onClose={onClose} />;
}

function WarehouseForm({
  warehouse,
  onClose,
}: {
  warehouse: Warehouse | null;
  onClose: () => void;
}) {
  const save = useSaveWarehouse();
  const [form, setForm] = useState<FormState>(() =>
    warehouse
      ? {
          name: warehouse.name,
          code: warehouse.code,
          addressLine1: warehouse.addressLine1,
          addressLine2: warehouse.addressLine2 ?? '',
          city: warehouse.city,
          state: warehouse.state,
          pincode: warehouse.pincode,
          managerName: warehouse.managerName ?? '',
          managerPhone: warehouse.managerPhone ?? '',
          isActive: warehouse.isActive,
        }
      : EMPTY,
  );

  const errors = fieldErrors(save.error);
  const set = (key: keyof FormState, value: string | boolean): void =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    save.mutate(
      {
        id: warehouse?.id ?? null,
        body: {
          name: form.name,
          code: form.code,
          addressLine1: form.addressLine1,
          addressLine2: form.addressLine2 || null,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          managerName: form.managerName || null,
          managerPhone: form.managerPhone || null,
          isActive: form.isActive,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={warehouse ? 'Edit warehouse' : 'New warehouse'}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="warehouse-form" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <form id="warehouse-form" onSubmit={handleSubmit} className="space-y-4">
        {save.isError && <ErrorAlert title="Could not save">{errorMessage(save.error)}</ErrorAlert>}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" htmlFor="wh-name" required error={errors['name']}>
            <Input
              id="wh-name"
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              required
              minLength={2}
              invalid={Boolean(errors['name'])}
            />
          </Field>

          <Field
            label="Code"
            htmlFor="wh-code"
            hint="Letters, digits and hyphens"
            error={errors['code']}
          >
            <Input
              id="wh-code"
              value={form.code}
              onChange={(event) => set('code', event.target.value.toUpperCase())}
              required
              placeholder="GGN-01"
              invalid={Boolean(errors['code'])}
            />
          </Field>
        </div>

        <Field label="Address line 1" htmlFor="wh-address1" required error={errors['addressLine1']}>
          <Input
            id="wh-address1"
            value={form.addressLine1}
            onChange={(event) => set('addressLine1', event.target.value)}
            required
            invalid={Boolean(errors['addressLine1'])}
          />
        </Field>

        <Field label="Address line 2" htmlFor="wh-address2">
          <Input
            id="wh-address2"
            value={form.addressLine2}
            onChange={(event) => set('addressLine2', event.target.value)}
          />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="City" htmlFor="wh-city" required error={errors['city']}>
            <Input
              id="wh-city"
              value={form.city}
              onChange={(event) => set('city', event.target.value)}
              required
              invalid={Boolean(errors['city'])}
            />
          </Field>

          <Field label="State" htmlFor="wh-state" required error={errors['state']}>
            <Input
              id="wh-state"
              value={form.state}
              onChange={(event) => set('state', event.target.value)}
              required
              invalid={Boolean(errors['state'])}
            />
          </Field>

          <Field label="Pincode" htmlFor="wh-pincode" required error={errors['pincode']}>
            <Input
              id="wh-pincode"
              value={form.pincode}
              onChange={(event) => set('pincode', event.target.value)}
              required
              inputMode="numeric"
              pattern="[1-9][0-9]{5}"
              invalid={Boolean(errors['pincode'])}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Manager" htmlFor="wh-manager">
            <Input
              id="wh-manager"
              value={form.managerName}
              onChange={(event) => set('managerName', event.target.value)}
            />
          </Field>

          <Field label="Manager phone" htmlFor="wh-phone">
            <Input
              id="wh-phone"
              value={form.managerPhone}
              onChange={(event) => set('managerPhone', event.target.value)}
            />
          </Field>
        </div>

        <Checkbox
          label="Active"
          checked={form.isActive}
          onChange={(event) => set('isActive', event.target.checked)}
        />

        {warehouse?.isDefault && (
          <p className="text-xs text-ink-subtle">
            This is the default warehouse. Promote another one before deactivating it.
          </p>
        )}
      </form>
    </Drawer>
  );
}
